import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { detectCountry } from '../utils/countryDetection';
import { COUNTRY_DIAL_CODES, getDialCode, getCountryCodeFromTimezone } from '../utils/phoneCountry';

const INDUSTRIES = [
  { id: 'Salon', icon: '💇‍♀️', label: 'Salon', desc: 'Hair, nails & beauty' },
  { id: 'Spa', icon: '🧖‍♀️', label: 'Spa', desc: 'Massage & wellness' },
  { id: 'Barbershop', icon: '💈', label: 'Barbershop', desc: 'Cuts & grooming' },
  { id: 'Car Wash', icon: '🚗', label: 'Car Wash', desc: 'Auto detailing' },
  { id: 'Dental Clinic', icon: '🦷', label: 'Dental Clinic', desc: 'Oral health care' },
  { id: 'Fitness Studio', icon: '🏋️', label: 'Fitness Studio', desc: 'Gym & training' },
  { id: 'Restaurant', icon: '🍽️', label: 'Restaurant', desc: 'Dining & food' },
  { id: 'Pet Grooming', icon: '🐾', label: 'Pet Grooming', desc: 'Pet care & styling' },
  { id: 'Pathology Lab', icon: '🔬', label: 'Pathology Lab', desc: 'Diagnostics & tests' },
  { id: 'Other', icon: '🏪', label: 'Other', desc: 'Something else' },
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'India',
  'Germany', 'France', 'Netherlands', 'Singapore', 'United Arab Emirates',
  'South Africa', 'New Zealand', 'Ireland', 'Sweden', 'Norway',
  'Denmark', 'Switzerland', 'Belgium', 'Austria', 'Italy',
  'Spain', 'Portugal', 'Brazil', 'Mexico', 'Japan',
  'South Korea', 'Philippines', 'Malaysia', 'Thailand', 'Indonesia',
  'Nigeria', 'Kenya', 'Ghana', 'Egypt', 'Saudi Arabia',
  'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Other',
];

// Expected local-number digit lengths per dial code, used only for input
// validation. The dial codes themselves come from the shared phoneCountry util
// (single source of truth); this table just refines the accepted length for the
// codes we know precisely. Anything not listed falls back to the E.164 range
// (7–15 digits), so every country still validates sensibly.
const PHONE_LENGTH_BY_CODE = {
  '+1': { minLen: 10, maxLen: 10 },
  '+44': { minLen: 10, maxLen: 10 },
  '+61': { minLen: 9, maxLen: 9 },
  '+91': { minLen: 10, maxLen: 10 },
  '+49': { minLen: 10, maxLen: 11 },
  '+33': { minLen: 9, maxLen: 9 },
  '+31': { minLen: 9, maxLen: 9 },
  '+65': { minLen: 8, maxLen: 8 },
  '+971': { minLen: 9, maxLen: 9 },
  '+27': { minLen: 9, maxLen: 9 },
  '+64': { minLen: 8, maxLen: 10 },
  '+353': { minLen: 9, maxLen: 9 },
  '+46': { minLen: 9, maxLen: 9 },
  '+47': { minLen: 8, maxLen: 8 },
  '+45': { minLen: 8, maxLen: 8 },
  '+41': { minLen: 9, maxLen: 9 },
  '+32': { minLen: 9, maxLen: 9 },
  '+43': { minLen: 10, maxLen: 13 },
  '+39': { minLen: 9, maxLen: 10 },
  '+34': { minLen: 9, maxLen: 9 },
  '+351': { minLen: 9, maxLen: 9 },
  '+55': { minLen: 10, maxLen: 11 },
  '+52': { minLen: 10, maxLen: 10 },
  '+81': { minLen: 10, maxLen: 11 },
  '+82': { minLen: 9, maxLen: 11 },
  '+63': { minLen: 10, maxLen: 10 },
  '+60': { minLen: 9, maxLen: 10 },
  '+66': { minLen: 9, maxLen: 9 },
  '+62': { minLen: 9, maxLen: 12 },
  '+234': { minLen: 10, maxLen: 10 },
  '+254': { minLen: 9, maxLen: 9 },
  '+233': { minLen: 9, maxLen: 9 },
  '+20': { minLen: 10, maxLen: 10 },
  '+966': { minLen: 9, maxLen: 9 },
  '+974': { minLen: 8, maxLen: 8 },
  '+965': { minLen: 8, maxLen: 8 },
  '+973': { minLen: 8, maxLen: 8 },
  '+968': { minLen: 8, maxLen: 8 },
};

const DEFAULT_PHONE_LEN = { minLen: 7, maxLen: 15 };

function validatePhoneNumber(dialCode, localNumber) {
  const digits = localNumber.replace(/\D/g, '');
  const { minLen: min, maxLen: max } = PHONE_LENGTH_BY_CODE[dialCode] || DEFAULT_PHONE_LEN;
  if (digits.length < min || digits.length > max) {
    return `Phone number must be ${min === max ? min : `${min}–${max}`} digits for ${dialCode}`;
  }
  return null;
}

export default function BusinessSetup() {
  const { markBusinessSetup, setBusinessName, isDemo } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    industry: '',
    phoneCode: '+1',
    phoneNumber: '',
    website: '',
    googleReviewLink: '',
    bookingUrl: '',
    country: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countryDetecting, setCountryDetecting] = useState(true);

  useEffect(() => {
    // Preselect the dial code from the browser timezone (Asia/Kolkata → +91)
    // via the shared phoneCountry util so this page and Customers stay in sync.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { code } = getCountryCodeFromTimezone(timezone);
    setForm((prev) => ({ ...prev, phoneCode: code }));

    detectCountry({
      countries: COUNTRIES,
      onDetected: (country) => {
        if (country) {
          setForm((prev) => ({ ...prev, country, phoneCode: getDialCode(country) }));
        }
        setCountryDetecting(false);
      },
    }).finally(() => setCountryDetecting(false));
  }, []);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const selectIndustry = (id) => {
    update('industry', id);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isDemo) {
      markBusinessSetup();
      setBusinessName('Glow Salon');
      navigate('/dashboard');
      return;
    }

    const phoneError = validatePhoneNumber(form.phoneCode, form.phoneNumber);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    setLoading(true);
    try {
      const phone = `${form.phoneCode}${form.phoneNumber.replace(/\D/g, '')}`;
      await api.createBusiness({ ...form, phone });
      markBusinessSetup();
      setBusinessName(form.name);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-page">
      {step === 1 ? (
        <div className="setup-card" style={{ maxWidth: 680 }}>
          <h1>What type of business do you run?</h1>
          <p className="subtitle">
            We'll customize your messages and templates based on your industry.
          </p>

          <div className="industry-grid">
            {INDUSTRIES.map((ind) => (
              <button
                key={ind.id}
                className={`industry-card ${form.industry === ind.id ? 'selected' : ''}`}
                onClick={() => selectIndustry(ind.id)}
                type="button"
              >
                <span className="industry-icon">{ind.icon}</span>
                <span className="industry-label">{ind.label}</span>
                <span className="industry-desc">{ind.desc}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="setup-card">
          <button
            type="button"
            onClick={() => setStep(1)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 14,
              marginBottom: 16,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            &larr; Change business type
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 32 }}>
              {INDUSTRIES.find((i) => i.id === form.industry)?.icon}
            </span>
            <div>
              <h1 style={{ marginBottom: 0 }}>Set up your {form.industry}</h1>
            </div>
          </div>
          <p className="subtitle">Fill in your details and you're good to go.</p>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Business Name</label>
              <input
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder={
                  form.industry === 'Salon' ? 'Glow Beauty Salon' :
                  form.industry === 'Car Wash' ? 'Sparkle Auto Wash' :
                  form.industry === 'Restaurant' ? 'The Hungry Fork' :
                  'Your Business Name'
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={form.phoneCode}
                  onChange={(e) => update('phoneCode', e.target.value)}
                  style={{
                    flexShrink: 0,
                    width: 'auto',
                    padding: '10px 6px',
                    border: '1.5px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  {COUNTRY_DIAL_CODES.map((p) => (
                    <option key={`${p.country}-${p.code}`} value={p.code}>
                      {p.flag} {p.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={form.phoneNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d\s\-().]/g, '');
                    update('phoneNumber', val);
                  }}
                  placeholder="9876543210"
                  required
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Enter your local number without the country code.
              </p>
            </div>
            <div className="form-group">
              <label>Website URL <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => update('website', e.target.value)}
                placeholder="https://yourbusiness.com"
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <div style={{ position: 'relative' }}>
                <select
                  value={form.country}
                  onChange={(e) => update('country', e.target.value)}
                  required
                  className="form-select"
                >
                  <option value="">Select your country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {countryDetecting && (
                  <span style={{ position: 'absolute', right: 36, top: '50%', transform: 'translateY(-50%)' }}>
                    <span className="spinner" style={{ width: 14, height: 14 }} />
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Pre-selected from your timezone. You can change it before continuing.
              </p>
            </div>
            <div className="form-group">
              <label>Google Review Link <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="url"
                value={form.googleReviewLink}
                onChange={(e) => update('googleReviewLink', e.target.value)}
                placeholder="https://g.page/yourbusiness/review"
              />
            </div>
            <div className="form-group">
              <label>
                Booking / Appointment URL{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="url"
                value={form.bookingUrl}
                onChange={(e) => update('bookingUrl', e.target.value)}
                placeholder="https://calendly.com/yourbusiness"
              />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                If provided, we'll include a trackable booking link in your reminder messages.
              </p>
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Launch LoopVisitor'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
